/* KANON `ankiety-klienta-2026-09-16` — the Ankiety tab's own surface: the chart strip (direction
 * B's, kept by the owner), and the STREAM of occurrences on the left of the pane.
 *
 * ONE stylesheet for the two because they are one surface in one visual language, which is the same
 * argument `DetailTab.module.css` makes for the three data tabs. Everything this tab shares with
 * those — the card, the range bar's two date fields, the three-column chart grid, the notice — is
 * imported from that file rather than re-declared here; what is below is only what the canon adds.
 *
 * The PANE is `SurveyPane.module.css`, and the way one ANSWER is drawn is
 * `SurveyReading.module.css`, because both are shared with surfaces this file does not own. */

/* ── the two columns ────────────────────────────────────────────────────────── */

/* `align-items: flex-start` so the stream card is exactly as tall as its own ceiling rather than
 * being stretched to the pane's height — which is what makes `position: sticky` inside it mean
 * anything. */
.split {
  display: flex;
  gap: var(--space-4);
  align-items: flex-start;
}

/* A fixed width with `flex: none`, and the card inside carries `min-width: 0`: an explicit width on
 * a flex item caps its MINIMUM, and nowrap content then overflows into the neighbour — a failure
 * this app has paid for once. */
.streamColumn {
  position: sticky;
  top: var(--space-0);
  flex: none;
  width: var(--w-survey-stream);
}

/* ── the stream ─────────────────────────────────────────────────────────────── */

/* It is the `DetailTab` card with its own ceiling. The rows scroll INSIDE it, which is not the
 * double scroll the owner banned on 16 September 2026: that one was a second bar beside the tab's
 * own over the SAME content. This is a two-hundred-row list standing beside a pane read at its own
 * length, and the tab's scroll still belongs to the pane. */
.streamCard {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-width: 0;
  max-block-size: var(--h-survey-stream-max);
  padding: var(--space-15px) var(--space-14px);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
}

.streamHead {
  display: flex;
  flex: none;
  gap: var(--space-2);
  align-items: center;
}

.streamHeading {
  margin: var(--space-0);
  font-size: var(--fs-detail-sec);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-heading);
  color: var(--text);
  letter-spacing: var(--ls-detail-sec);
}

/* How many answers are still waiting to be read.
 *
 * NEUTRAL — owner, 16 September 2026. The ember on this card is the DOT on a row still waiting, and
 * a second ember object four pixels from the heading competed with it for the one thing the accent
 * is spent on here. The badge is a chip like any other; the count is what it says. */
.streamCount {
  display: inline-grid;
  place-items: center;
  min-width: var(--sz-survey-mark);
  height: var(--sz-survey-mark);
  padding: var(--space-0) var(--space-5px);
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-auto);
  color: var(--text);
  background: var(--bg-chip);
  border-radius: var(--radius-sm);
  font-variant-numeric: tabular-nums;
}

/* GEOMETRY ONLY — the treatment is `shared/ui/iconButton.module.css`. The compare control and the
 * way out of compare mode stand in the same slot, so they are the same square. */
.streamAction {
  flex: none;
  width: var(--h-detail-tiny-btn);
  height: var(--h-detail-tiny-btn);
  margin-inline-start: auto;
  background: var(--bg-chip);
  border-radius: var(--radius-mark);
}

/* „Porównujesz: wybierz 2" — what the checkboxes below are for, said in the head they appeared in. */
.streamMode {
  flex: 1;
  margin-inline-start: auto;
  font-size: var(--fs-eyebrow);
  font-style: normal;
  font-weight: var(--fw-bold);
  line-height: var(--lh-normal);
  color: var(--text);
  text-align: end;
}

.streamChips {
  display: flex;
  flex: none;
  flex-wrap: wrap;
  gap: var(--space-5px);
}

/* The form filter. The chosen one is the INVERTED chip the canon draws — ink ground, page ink. Not
 * the accent: a filter position is not an act, and the accent on this tab marks the one control
 * that is. */
.formChip {
  display: inline-flex;
  flex: none;
  align-items: center;
  height: var(--h-survey-chip-tall);
  padding: var(--space-0) var(--space-3);
  font-family: var(--font-body);
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  cursor: pointer;
  background: var(--bg-chip);
  border: none;
  border-radius: var(--r-pill);
}

.formChipOn {
  color: var(--bg);
  background: var(--text);
}

.formChip:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-2px);
}

/* The rows' own port. `scrollbar-gutter` is deliberately not set: a short history fits, and a
 * reserved gutter under a list of four rows would be a strip of empty card beside every one of
 * them. */
.streamScroller {
  --sb-size: var(--space-2);
  --sb-radius: var(--radius-hair);

  flex: 1;
  min-height: var(--h-survey-stream-min);
  overflow-y: auto;
}

.streamMonth {
  display: flex;
  flex-direction: column;
  gap: var(--space-1px);
}

.streamEyebrow {
  padding: var(--space-2) var(--space-3) var(--space-1px);
  font-size: var(--fs-micro-label);
  font-style: normal;
  font-weight: var(--fw-bold);
  line-height: var(--lh-normal);
  color: var(--text-2);
  text-transform: uppercase;
  letter-spacing: var(--ls-lab);
}

/* One occurrence. A control in both modes, and a paragraph when it was never answered — so the
 * declarations are shared and only the ground and the ink differ. */
.streamRow {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  width: 100%;
  padding: var(--space-2) var(--space-3);
  margin: var(--space-0);
  font-family: var(--font-body);
  text-align: start;
  cursor: pointer;
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
}

.streamRow:hover {
  background: var(--bg-inset);
}

/* The row the pane is about. `--bg-chip` and a heavier title — two signals, because a ground alone
 * is a difference a trainer reads as "slightly lighter" rather than as "this one". The heavier
 * title is declared below, after `.streamTitle` itself: a descendant selector outranks it, and
 * stylelint refuses the two in the other order. */
.streamRowOn {
  background: var(--bg-chip);
}

.streamRowOn:hover {
  background: var(--bg-chip);
}

/* A missed occurrence: the fact belongs on the list, the affordance does not. Dimmed through the
 * INK rather than through `opacity`, so the chip beside it keeps its own contrast. */
.streamRowMissed {
  cursor: default;
}

.streamRow:focus-visible {
  outline: var(--border-focus) solid var(--accent);

  /* Flush, not outset: the rows scroll inside a port with `overflow-y: auto`, and an outline drawn
   * outside the row is clipped at the port's own edge on the first and last of them. */
  outline-offset: var(--space-0);
}

/* The checkbox that appears in compare mode. Drawn rather than an `<input>`, because a refused row
 * has to keep `aria-disabled` and stay focusable — a `disabled` input drops out of the tab order
 * and takes the reason attached to it with it. */
.streamBox {
  box-sizing: border-box;
  flex: none;
  width: var(--sz-survey-box);
  height: var(--sz-survey-box);
  background: transparent;
  border: var(--border-ring) solid var(--border-2);
  border-radius: var(--radius-hair);
}

/* A row that cannot be checked still stands in the same column as the ones that can: the box's
 * SPACE is kept while compare mode is on, so a missed week does not jut out to the left of every
 * row above it. */
.streamBoxGap {
  flex: none;
  width: var(--sz-survey-box);
}

.streamRow[aria-checked='true'] .streamBox {
  background: var(--text);
  border-color: var(--text);
}

/* A row of another form, while a comparison is anchored. `aria-disabled` rather than `disabled`, so
 * the reason attached to it stays reachable — and the reason has to be READ, which is why the row
 * is quieter in INK and not behind an `opacity`. A dim took the „po terminie" chip and the date
 * down with the title and made the one row the trainer was looking for the hardest to find. */
.streamRow[aria-disabled='true'] {
  cursor: default;
}

.streamText {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-2px);
  min-width: 0;
}

.streamTitle {
  overflow: hidden;
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  line-height: var(--lh-normal);
  color: var(--text);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.streamWhen {
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-2);
  font-variant-numeric: tabular-nums;
}

/* The three overrides that have to stand after the classes they override — see `.streamRowOn`.
 * Stylelint refuses a descendant selector declared before the class it outranks. */
.streamRowOn .streamTitle {
  font-weight: var(--fw-extrabold);
  color: var(--text);
}

/* A week she did not answer. Quieter than a row that can be opened through WEIGHT — the ink stays
 * readable, because „pominięta · pon 3 sie" is the whole point of drawing the row at all. */
.streamRowMissed .streamTitle {
  font-weight: var(--fw-medium);
  color: var(--text-2);
}

/* A row of another form while a comparison is anchored. Quieter in WEIGHT and one step of ink, not
 * behind an `opacity` — see `.streamRow[aria-disabled='true']`. */
.streamRow[aria-disabled='true'] .streamTitle {
  font-weight: var(--fw-medium);
  color: var(--text-2);
}

/* The read failed, or there is nothing to list. A sentence, not a second `EmptyState`: the drawing
 * and the tone stand in the pane beside this card. */
.streamNote {
  padding: var(--space-4) var(--space-3);
  margin: var(--space-0);
  font-size: var(--fs-caption);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-2);
}

/* The review dot, and there is only one: solid ember on an occurrence still waiting to be read.
 * „Przejrzana" is the ABSENCE of it — owner, 16 September 2026 — because a grey dot on every row
 * already read marked the majority with something nobody acts on and made the ember one harder to
 * find. The row's own accessible name says what the mark means, which is why the legend went too. */
.dot {
  flex: none;
  width: var(--sz-survey-dot);
  height: var(--sz-survey-dot);
  background: var(--accent);
  border-radius: var(--r-circle);
}

/* ── an occurrence's outcome ────────────────────────────────────────────────── */

.statusChip {
  display: inline-flex;
  flex: none;
  align-items: center;
  height: var(--h-survey-chip);
  padding: var(--space-0) var(--space-2);
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  white-space: nowrap;
  background: var(--bg-chip);
  border-radius: var(--r-pill);
  letter-spacing: var(--ls-chip);
}

.statusWarn {
  color: var(--warn-ink);
  background: var(--warn-soft);
}

/* `--text-2` rather than `--text-3`: „pominięta" is a SENTENCE, and the lighter token measured
 * 3,01:1 on this card in Flame. It carries no text that has to be read. */
.statusMissed {
  color: var(--text-2);
  background: transparent;
  border: var(--border-hairline) dashed var(--border-2);
}

/* ── the chart strip ────────────────────────────────────────────────────────── */

/* Four or more measurable questions do not fit three equal columns, and stacking them would turn a
 * comparison into a scroll of the page. So they scroll SIDEWAYS in the form's own question order,
 * one card snapping to the start of the port at a time — fit or scroll, never clip.
 *
 * `overflow: auto hidden`: the vertical axis must stay `hidden` or the port grows a second bar for
 * a card that is exactly as tall as it is. */
.chartScroller {
  --sb-size: var(--space-2);
  --sb-radius: var(--radius-hair);

  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: var(--w-survey-chart-card);
  gap: var(--space-5);
  overflow: auto hidden;
  scroll-snap-type: x mandatory;
}

.chartCard {
  scroll-snap-align: start;
}

/* The ONE card the row draws when the read failed or the form asks nothing measurable. It spans the
 * whole three-column grid, so the row is the same width in all four states — without this it stood
 * in the first track and read as a chart that had lost its two neighbours. */
.chartSpan {
  grid-column: 1 / -1;
}

/* The card head, over TWO lines: the question, then its reading and change under it.
 *
 * `DetailTab`'s `.chartHead` is one line with the figure beside the name, which is right for Trendy
 * — „TONAŻ" is a word. A trainer's own question is a sentence, and ellipsising it („CZY WYKONAŁEŚ
 * WSZYS…") leaves the card saying nothing about what it measures. So this head is a column, and its
 * box is RESERVED at the two-line height whether or not the second line is used: cards stand side
 * by side in one row and a head that grew when its data landed would move every plot beside it. */
.chartHead {
  flex-direction: column;
  gap: var(--space-1);
  align-items: flex-start;
  min-height: var(--h-survey-chart-head);
}

/* The reading and its change, on their own line under the question.
 *
 * `margin-block-start: auto` pushes it to the BOTTOM of the head's reserve, so every figure in the
 * row stands on one line whether its question took one line or two. Without it a two-line question
 * dropped its figure 13px below its neighbours' and the row read as ragged. Baseline-aligned inside
 * itself, so the change sits on the figure's own line rather than on the middle of it. */
.chartReading {
  display: flex;
  gap: var(--space-2);
  align-items: baseline;
  margin-block-start: auto;
}

/* ── the two-option card ────────────────────────────────────────────────────── */

/* The ring and the occurrences it is a share OF, side by side inside the plot's own reserve — so a
 * choice card is exactly as tall as the line cards beside it and the row has one baseline. */
.binary {
  display: flex;
  gap: var(--space-4);
  align-items: center;
  block-size: var(--chart-card-reserve);
}

.binaryStrip {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
}

/* The SECOND option. Not the attendance grid's „missed" cell, which wears `--danger`: a two-option
 * question is not necessarily yes/no — „zielony"/„niebieski" is just as valid — so neither option
 * may be painted as the bad one. An OUTLINE against the first option's fill, in the neutral edge
 * this app already draws every quiet border in. */
.optCellB {
  border: var(--border-hairline) solid var(--border-2);
}

/* Which drawing is which answer, and how many of each. A fill beside an outline says nothing until
 * something names them, and this card may not say it with colour. */
.binaryLegend {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2) var(--space-3);
  align-items: center;
  padding: var(--space-0);
  margin: var(--space-0);
  font-size: var(--fs-tag);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-2);
  list-style: none;
}

.binaryLegendItem {
  display: inline-flex;
  gap: var(--space-5px);
  align-items: center;
  min-width: 0;
}

.binaryCount {
  font-weight: var(--fw-bold);
  color: var(--text);
  font-variant-numeric: tabular-nums;
}

/* The legend's own swatch: the cell drawing at a legend's size. */
.binarySwatch {
  flex: none;
  width: var(--sz-survey-swatch);
}

/* The window's two ends. The dates BETWEEN them are on the stream to the left, so printing one per
 * cell would be ten labels saying what one list already says. */
.binaryDates {
  display: flex;
  justify-content: space-between;
  margin: var(--space-0);
  font-size: var(--fs-tag);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-2);
  font-variant-numeric: tabular-nums;
}

/* ── the wait ───────────────────────────────────────────────────────────────── */

.skelStream {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
}

.skelStreamRow {
  width: 100%;
  height: var(--h-survey-stream-row);
}

/* ── „Przypisz ankietę" (8 October 2026) ──────────────────────────────────── */

/* GEOMETRY ONLY — the treatment is `shared/ui/ghostButton`. On the range bar it wears the date
 * fields' own height, so the bar is exactly as tall as it was before the control arrived. */
.assignBar {
  height: var(--h-range-field);
  padding: 0 var(--space-11px);
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  background: var(--bg-card);
  border-radius: var(--radius-8px);
}

/* The empty pane: the sentence, and the act it names under it. */
.emptyPane {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-3);
  align-items: center;
  justify-content: center;
}

.assignEmpty {
  height: var(--h-detail-btn);
  padding: 0 var(--space-14px);
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  background: var(--bg-card);
  border-radius: var(--radius-mark);
}

/* „Przypisane ankiety": the range bar's card and its one row, in every state. The chips scroll
 * sideways inside it rather than wrapping, so a long list never makes the bar taller. */
.enrolledBar {
  display: flex;
  flex: none;
  gap: var(--space-10px);
  align-items: center;
  min-height: calc(var(--h-range-field) + 2 * var(--space-11px));
  padding: var(--space-11px) var(--space-14px);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-md);
}

.enrolledBar .enrolledEyebrow {
  flex: none;
}

.enrolledList {
  display: flex;
  flex: 1;
  align-items: center;
  min-width: 0;
  overflow-x: auto;
  overscroll-behavior-x: contain;
}

.enrolledChips {
  display: flex;
  gap: var(--space-2);
  margin: var(--space-0);
  padding: var(--space-0);
  list-style: none;
}

.enrolledChip {
  gap: var(--space-1);
  text-decoration: none;
}

.enrolledChip:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-1px);
}

.enrolledTitle {
  color: var(--text);
}

.enrolledNote {
  font-size: var(--fs-label);
  font-weight: var(--fw-medium);
  color: var(--text-2);
  white-space: nowrap;
}

.enrolledSkel {
  width: 40%;
  height: var(--space-3);
}
